// addInsertColumn — VQE-Edit edit-action: append a column to an
// `INSERT INTO t (cols…) VALUES (…), (…)` statement's explicit column list
// via SR-Edit's `add-insert-column` op. SR-Edit plans the multi-range edit
// (the column-list insert PLUS one value cell per VALUES row, keeping every
// row shape-consistent — §2.4 S6); this module only validates the inputs
// against an optional structure and delegates to `commitEdit`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.

import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';

export type AddInsertColumnOptions = {
  /**
   * Optional structure used for shape validation. Lets the caller surface a
   * friendlier error than SR-Edit's silent "no edits" response when the
   * statement has no explicit column list / no VALUES rows to extend
   * (add/remove-insert-column are VALUES-form-only — decision 4).
   */
  readonly structure?: QueryStructure;
  /**
   * Value text seeded into every row's new cell. Defaults to `NULL` at the
   * applicator when omitted — the v1 UI always omits this (decision 6); the
   * option exists so the op's `valueText?` carries through for future callers
   * (e.g. a `DEFAULT` fill).
   */
  readonly valueText?: string;
};

export async function addInsertColumn(
  columnText: string,
  ctx: CommitEditCtx,
  options: AddInsertColumnOptions = {},
): Promise<CommitOutcome> {
  const trimmedColumn = columnText.trim();
  if (trimmedColumn.length === 0) {
    throw new Error('addInsertColumn: columnText must not be empty');
  }
  if (options.structure) {
    const cols = options.structure.insertColumns;
    if (!cols || cols.length === 0) {
      throw new Error('addInsertColumn: statement has no explicit column list to extend');
    }
    const rows = options.structure.insertValueRowRanges;
    if (!rows || rows.length === 0) {
      throw new Error('addInsertColumn: statement has no VALUES rows to extend');
    }
  }
  const trimmedValue = options.valueText?.trim();
  return commitEdit(
    {
      op: 'add-insert-column',
      columnText: trimmedColumn,
      ...(trimmedValue ? { valueText: trimmedValue } : {}),
    },
    ctx,
  );
}
